import React, { useState } from 'react';
import { StatusCard } from './ui';
import type { StatusType } from './ui';
import './ConnectionsPanel.css';

interface DropboxStatus {
  connected: boolean;
  accountId?: string;
  email?: string;
  expiresAt?: string;
  createdAt?: string;
}

interface GarminStatus {
  connected: boolean;
  email?: string;
  sessionCreated?: string;
}

interface StravaStatus {
  connected: boolean;
  athleteId?: string;
  firstName?: string;
  lastName?: string;
  createdAt?: string;
}

interface BarrysStatus {
  connected: boolean;
  valid?: boolean;
  email?: string;
  createdAt?: string;
}

interface ConnectionsPanelProps {
  dropboxStatus: DropboxStatus | null;
  garminStatus: GarminStatus | null;
  stravaStatus: StravaStatus | null;
  barrysStatus?: BarrysStatus | null;
  isCheckingDropbox: boolean;
  isCheckingGarmin: boolean;
  isCheckingStrava: boolean;
  isCheckingBarrys?: boolean;
  onConnectDropbox: () => void;
  onDisconnectDropbox: () => void;
  onConnectStrava: () => void;
  onDisconnectStrava: () => void;
  onConnectBarrys?: (username: string, password: string) => Promise<void>;
  onDisconnectBarrys?: () => void;
}

export const ConnectionsPanel: React.FC<ConnectionsPanelProps> = ({
  dropboxStatus,
  garminStatus,
  stravaStatus,
  barrysStatus,
  isCheckingDropbox,
  isCheckingGarmin,
  isCheckingStrava,
  isCheckingBarrys,
  onConnectDropbox,
  onDisconnectDropbox,
  onConnectStrava,
  onDisconnectStrava,
  onConnectBarrys,
  onDisconnectBarrys,
}) => {
  const [barrysUsername, setBarrysUsername] = useState('');
  const [barrysPassword, setBarrysPassword] = useState('');
  const [isConnectingBarrys, setIsConnectingBarrys] = useState(false);
  const [showBarrysForm, setShowBarrysForm] = useState(false);

  const getDropboxStatusType = (): StatusType => {
    if (isCheckingDropbox) return 'loading';
    if (!dropboxStatus) return 'disconnected';
    return dropboxStatus.connected ? 'connected' : 'disconnected';
  };

  const getGarminStatusType = (): StatusType => {
    if (isCheckingGarmin) return 'loading';
    if (!garminStatus) return 'disconnected';
    return garminStatus.connected ? 'connected' : 'disconnected';
  };

  const getStravaStatusType = (): StatusType => {
    if (isCheckingStrava) return 'loading';
    if (!stravaStatus) return 'disconnected';
    return stravaStatus.connected ? 'connected' : 'disconnected';
  };

  const getBarrysStatusType = (): StatusType => {
    if (isCheckingBarrys) return 'loading';
    if (!barrysStatus) return 'disconnected';
    if (barrysStatus.connected && !barrysStatus.valid) return 'warning';
    return barrysStatus.connected ? 'connected' : 'disconnected';
  };

  const [barrysError, setBarrysError] = useState('');

  const handleBarrysConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onConnectBarrys || !barrysUsername.trim() || !barrysPassword.trim()) return;

    setIsConnectingBarrys(true);
    setBarrysError('');
    try {
      await onConnectBarrys(barrysUsername.trim(), barrysPassword.trim());
      setBarrysUsername('');
      setBarrysPassword('');
      setShowBarrysForm(false);
    } catch (err: any) {
      setBarrysError(err.message || 'Connection failed');
    } finally {
      setIsConnectingBarrys(false);
    }
  };

  const closeBarrysModal = () => {
    if (!isConnectingBarrys) {
      setShowBarrysForm(false);
      setBarrysUsername('');
      setBarrysPassword('');
      setBarrysError('');
    }
  };

  const showBarrys = barrysStatus !== undefined;

  return (
    <div className="connections-panel">
      <div className="connections-panel__header">
        <h2 className="connections-panel__title">Connections</h2>
        <p className="connections-panel__subtitle">Manage your service accounts</p>
      </div>

      <div className="connections-panel__grid">
        <StatusCard
          title="Garmin"
          icon="🏃"
          status={getGarminStatusType()}
          accountInfo={garminStatus?.email}
          additionalInfo={
            garminStatus?.sessionCreated ? new Date(garminStatus.sessionCreated).toLocaleDateString() : undefined
          }
          connectLabel="Connected via login"
        />

        <StatusCard
          title="Dropbox"
          icon="📁"
          status={getDropboxStatusType()}
          accountInfo={dropboxStatus?.email}
          additionalInfo={dropboxStatus?.createdAt ? new Date(dropboxStatus.createdAt).toLocaleDateString() : undefined}
          onConnect={onConnectDropbox}
          onDisconnect={onDisconnectDropbox}
          connectLabel="Connect to Dropbox"
        />

        <StatusCard
          title="Strava"
          icon="🚴"
          status={getStravaStatusType()}
          accountInfo={
            stravaStatus?.firstName && stravaStatus?.lastName
              ? `${stravaStatus.firstName} ${stravaStatus.lastName}`
              : undefined
          }
          additionalInfo={stravaStatus?.createdAt ? new Date(stravaStatus.createdAt).toLocaleDateString() : undefined}
          onConnect={onConnectStrava}
          onDisconnect={onDisconnectStrava}
          connectLabel="Connect to Strava"
        />

        {showBarrys && (
          <StatusCard
            title="Barry's"
            icon="💪"
            status={getBarrysStatusType()}
            accountInfo={barrysStatus?.email}
            additionalInfo={barrysStatus?.createdAt ? new Date(barrysStatus.createdAt).toLocaleDateString() : undefined}
            onConnect={() => setShowBarrysForm(true)}
            onDisconnect={onDisconnectBarrys}
            connectLabel="Connect"
            warningMessage={
              barrysStatus?.connected && !barrysStatus?.valid ? 'Session expired. Reconnect to refresh.' : undefined
            }
          />
        )}
      </div>

      {showBarrysForm && (
        <div className="modal-overlay" onClick={closeBarrysModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">💪 Connect Barry&apos;s Bootcamp</h3>
            <p className="modal-description">Sign in with your Barry&apos;s Bootcamp account credentials.</p>
            <form onSubmit={handleBarrysConnect}>
              <div className="connections-panel__modal-form">
                <input
                  type="email"
                  value={barrysUsername}
                  onChange={(e) => {
                    setBarrysUsername(e.target.value);
                    setBarrysError('');
                  }}
                  placeholder="Email"
                  className="login-input"
                  disabled={isConnectingBarrys}
                  autoFocus
                />
                <input
                  type="password"
                  value={barrysPassword}
                  onChange={(e) => {
                    setBarrysPassword(e.target.value);
                    setBarrysError('');
                  }}
                  placeholder="Password"
                  className="login-input"
                  disabled={isConnectingBarrys}
                />
                {barrysError && <div className="modal-error">{barrysError}</div>}
                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={closeBarrysModal}
                    className="modal-button modal-button--secondary"
                    disabled={isConnectingBarrys}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="modal-button modal-button--primary"
                    disabled={isConnectingBarrys || !barrysUsername.trim() || !barrysPassword.trim()}
                  >
                    {isConnectingBarrys ? 'Connecting...' : 'Connect'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
